/**
 * Request coalescing + result caching, keyed by `requestKey`.
 *
 * THE TRAP THIS AVOIDS. Sharing one in-flight request between two widgets is
 * easy to get wrong in a way that reintroduces the exact bug the data contract
 * exists to prevent. If the shared fetch simply used the first caller's
 * `signal`, then the moment that widget issued a new request — a sort, a page
 * change, anything — it would abort the fetch the SECOND widget is still waiting
 * on. One widget's interaction silently breaks another's.
 *
 * So the shared request gets its OWN AbortController, and callers are
 * ref-counted. A caller leaving (its signal aborts) decrements; the shared
 * request is only aborted when the last caller has gone. Each caller still
 * observes its own abort immediately — it just doesn't take the others down.
 *
 * This module performs no I/O. It wraps a `RySource` and calls it.
 */

import { isAbort, requestKey, type RyRequest, type RyResponse, type RySource } from './ry-data.js';

interface InFlight {
  promise: Promise<RyResponse>;
  controller: AbortController;
  refs: number;
}

interface CacheEntry {
  response: RyResponse;
  at: number;
}

export interface CoalesceOptions {
  /** Serve a cached response for this many ms. 0 (default) disables caching. */
  cacheMs?: () => number;
  /**
   * Extra window past `cacheMs` during which a STALE response is served
   * immediately while a refresh runs behind it. 0 (default) disables SWR.
   */
  staleMs?: () => number;
  /** Called when a background revalidation produces a newer response. */
  onRevalidated?: (response: RyResponse) => void;
  /** Injected for tests. Defaults to Date.now. */
  now?: () => number;
}

/** Rejection used when a caller abandons a shared request. */
function abortError(): Error {
  const err = new Error('The operation was aborted.');
  err.name = 'AbortError';
  return err;
}

export class RyCoalescer {
  #inFlight = new Map<string, InFlight>();
  #cache = new Map<string, CacheEntry>();
  #options: CoalesceOptions;

  constructor(options: CoalesceOptions = {}) {
    this.#options = options;
  }

  /** Drop every cached response. Called on invalidate(). */
  clear(): void {
    this.#cache.clear();
  }

  get inFlightCount(): number {
    return this.#inFlight.size;
  }

  /** Wrap a fetcher so identical concurrent requests share one call. */
  wrap(fetcher: RySource): RySource {
    return (req: RyRequest) => this.#run(fetcher, req);
  }

  async #run(fetcher: RySource, req: RyRequest): Promise<RyResponse> {
    const key = requestKey(req);

    const cached = this.#fromCache(key, fetcher, req);
    if (cached) return cached;

    const entry = this.#inFlight.get(key) ?? this.#start(fetcher, req, key);
    entry.refs++;

    // Release SYNCHRONOUSLY on abort, not in the finally block.
    //
    // The runner aborts its previous request and then immediately issues the
    // next one, in the same synchronous turn. If the refcount only dropped when
    // the awaited promise settled — a microtask later — the superseding request
    // would find the old entry still alive and JOIN it. When the old request is
    // one that hangs, that's a deadlock: the caller who could have rescued it by
    // starting fresh is now waiting on it instead.
    let released = false;
    const release = (): void => {
      if (released) return;
      released = true;
      entry.refs--;
      // Last one out turns off the lights: a shared request nobody is waiting
      // for anymore is wasted bytes.
      if (entry.refs <= 0 && this.#inFlight.get(key) === entry) {
        this.#inFlight.delete(key);
        entry.controller.abort();
      }
    };

    if (req.signal.aborted) {
      release();
      throw abortError();
    }
    const onAbort = (): void => release();
    req.signal.addEventListener('abort', onAbort, { once: true });

    try {
      return await this.#raceCallerAbort(entry, req.signal);
    } finally {
      req.signal.removeEventListener('abort', onAbort);
      release();
    }
  }

  #start(fetcher: RySource, req: RyRequest, key: string): InFlight {
    // The shared request owns its own signal — deliberately NOT the caller's.
    const controller = new AbortController();
    const entry: InFlight = {
      controller,
      refs: 0,
      promise: Promise.resolve(fetcher({ ...req, signal: controller.signal })).then(
        (response) => {
          this.#toCache(key, response);
          if (this.#inFlight.get(key) === entry) this.#inFlight.delete(key);
          return response;
        },
        (error: unknown) => {
          if (this.#inFlight.get(key) === entry) this.#inFlight.delete(key);
          throw error;
        }
      ),
    };
    // Nobody may be awaiting yet; don't let a rejection go unhandled.
    entry.promise.catch(() => {});
    this.#inFlight.set(key, entry);
    return entry;
  }

  /**
   * Resolve with the shared result, UNLESS this caller's own signal fires first
   * — in which case this caller sees an abort while the others carry on.
   */
  #raceCallerAbort(entry: InFlight, signal: AbortSignal): Promise<RyResponse> {
    if (signal.aborted) return Promise.reject(abortError());
    return new Promise<RyResponse>((resolve, reject) => {
      const onAbort = (): void => reject(abortError());
      signal.addEventListener('abort', onAbort, { once: true });
      entry.promise.then(
        (r) => { signal.removeEventListener('abort', onAbort); resolve(r); },
        (e: unknown) => {
          signal.removeEventListener('abort', onAbort);
          // The shared request being aborted because everyone left is not this
          // caller's error — but if we're here, this caller hasn't left.
          reject(isAbort(e) ? abortError() : e);
        }
      );
    });
  }

  /**
   * Fresh hit → serve it. Stale-but-within-`staleMs` → serve it AND refresh
   * behind it (that's SWR: the user sees data instantly, and it corrects
   * itself a moment later). Past both → miss.
   */
  #fromCache(key: string, fetcher: RySource, req: RyRequest): RyResponse | null {
    const fresh = this.#options.cacheMs?.() ?? 0;
    const stale = this.#options.staleMs?.() ?? 0;
    if (fresh <= 0) return null;
    const hit = this.#cache.get(key);
    if (!hit) return null;

    const age = (this.#options.now?.() ?? Date.now()) - hit.at;
    if (age <= fresh) return hit.response;

    if (stale > 0 && age <= fresh + stale) {
      this.#revalidate(key, fetcher, req);
      return hit.response;
    }

    this.#cache.delete(key);
    return null;
  }

  /** Refresh in the background. Never throws into the caller's path. */
  #revalidate(key: string, fetcher: RySource, req: RyRequest): void {
    if (this.#inFlight.has(key)) return;   // already refreshing
    const entry = this.#start(fetcher, req, key);
    entry.refs++;
    entry.promise
      .then((response) => this.#options.onRevalidated?.(response))
      .catch(() => { /* a failed background refresh keeps the stale data */ })
      .finally(() => { entry.refs--; });
  }

  #toCache(key: string, response: RyResponse): void {
    if ((this.#options.cacheMs?.() ?? 0) <= 0) return;
    this.#cache.set(key, { response, at: this.#options.now?.() ?? Date.now() });
  }
}
